<template>
    <a-spin :spinning="loading" tip="Carregando...">
        <!-- filtro por nome -->
        <div class="filter-bar">
            <a-input-search
                v-model:value="filterText"
                placeholder="Filtrar por nome"
                allow-clear
                @search="applyFilter"
                style="max-width: 240px"
            />
        </div>

        <a-table
            :columns="columns"
            :data-source="filteredRows"
            :rowKey="rowKeyFn"
            :pagination="{ pageSize: 8, showSizeChanger: false, showTotal: (total) => `Total: ${total}` }"
            :rowSelection="rowSelection"
            :scroll="{ y: 320 }"
            size="small"
            bordered
        >
        
            <template #bodyCell="{ column, record }">
                <!-- Nome -->
                <template v-if="column.key === 'name'">
                    <a-typography-text>{{ record.name }}</a-typography-text>
                </template>

                <!-- Miniatura -->
                <template v-else-if="column.key === 'thumb'">
                    <div class="thumb-wrapper">
                        <img
                            class="thumb-img"
                            :src="record.path"
                            :alt="record.name"
                            @error="onImgError"
                        />
                    </div>
                </template>
            </template>
        </a-table>

        <div class="footer">
            <a-space>
                <a-button @click="$emit('cancel')">Fechar</a-button>
                <a-button type="primary" :disabled="!internalSelectedPath" @click="emitSave">Salvar</a-button>
            </a-space>
        </div>
    </a-spin>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
    selectedImagePath: { type: [String, null], default: null },
    endpoint: { type: String, default: '/api/configurations/client-images' }
})
const emit = defineEmits(['save', 'cancel'])

const loading = ref(false)
const rows = ref([])
const filterText = ref('')

/** seleção baseada no path */
const selectedRowKeys = ref(props.selectedImagePath ? [props.selectedImagePath] : [])
const internalSelectedPath = computed(() =>
    selectedRowKeys.value.length ? selectedRowKeys.value[0] : null
)

/** colunas */
const columns = [
    { title: 'Nome', dataIndex: 'name', key: 'name', sorter: (a, b) => String(a.name).localeCompare(String(b.name)) },
    { title: 'Miniatura', key: 'thumb', width: 180 }
]

const rowKeyFn = (row) => row.path

const rowSelection = computed(() => ({
    type: 'radio',
    selectedRowKeys: selectedRowKeys.value,
    onChange: (keys) => {
        selectedRowKeys.value = keys
    }
}))

/** aplica filtro local */
const filteredRows = computed(() => {
    if (!filterText.value) return rows.value
    const q = filterText.value.toLowerCase()
    return rows.value.filter(r => r.name.toLowerCase().includes(q))
})

function applyFilter () {
    // no client-side basta atualizar filterText, o computed já reage
}

async function load () {
    try {
        loading.value = true
        const { data } = await window.axios.get(props.endpoint)
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        rows.value = list
            .filter(item => !!item?.path)
            .map(item => ({
                id: item.id ?? null,
                name: item.name ?? item.path.split('/').pop(),
                path: item.path
            }))

        if (props.selectedImagePath && rows.value.some(r => r.path === props.selectedImagePath)) {
            selectedRowKeys.value = [props.selectedImagePath]
        } else if (props.selectedImagePath) {
            selectedRowKeys.value = []
        }
    } finally {
        loading.value = false
    }
}

function emitSave () {
    if (!internalSelectedPath.value) return
    const row = rows.value.find(r => r.path === internalSelectedPath.value)
    if (row) {
        emit('save', { id: row.id, path: row.path })
    } else {
        emit('save', internalSelectedPath.value)
    }
}

function onImgError (ev) {
    ev.target.src =
        'data:image/svg+xml;utf8,' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120">' +
        '<rect width="100%" height="100%" fill="%23f5f5f5"/>' +
        '<text x="50%" y="50%" font-size="12" text-anchor="middle" fill="%23999" dy=".3em">sem preview</text>' +
        '</svg>'
}

onMounted(load)

watch(
    () => props.selectedImagePath,
    (val) => { selectedRowKeys.value = val ? [val] : [] }
)
</script>

<style scoped>
.filter-bar {
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-start;
}

.footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

.thumb-wrapper {
    width: 160px;
    height: 120px;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    overflow: hidden;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
}

.thumb-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
</style>
